import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getBudgetHeads, addBudgetHead } from '../../services/AppService';
import closeButton from "../../assets/images/close.png";
import PageFade from '../../components/Login/PageFade';
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import ConfirmActionModal from "../../components/AdminComponents/Modals/ConfirmActionModal";
import BudgetHeadModal from './BudgetHeadModal';

const BudgetHeadPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Data State
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Pagination & Search states
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Modal States
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ open: false, formData: null });
    const [messageModal, setMessageModal] = useState({ open: false, type: "success", title: "", message: "" });

    // Handle Search Debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(0); // Reset to first page on search
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    const fetchBudgetHeads = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getBudgetHeads(debouncedSearch, page, size);
            if (res) {
                setData(res.content || []);
                setTotalPages(res.totalPages || 0);
                setTotalElements(res.totalElements || 0);
            }
        } catch (error) {
            console.error("Error fetching budget heads", error);
            showError("Failed to load budget head records");
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, page, size]);

    useEffect(() => { fetchBudgetHeads(); }, [fetchBudgetHeads]);

    // Handle form submission to intercept with Confirmation Modal
    const handleFormSubmit = (formData) => {
        setIsModalOpen(false);
        setConfirmModal({ open: true, formData: formData });
    };

    const handleConfirmSave = async () => {
        const { formData } = confirmModal;
        try {
            await addBudgetHead(formData);
            showSuccess("Budget Head added successfully");
            fetchBudgetHeads();
        } catch (error) {
            const backendError = error.response?.data?.message || "Failed to save record";
            showError(backendError);
        } finally {
            setConfirmModal({ open: false, formData: null });
        }
    };

    const showSuccess = (msg) => setMessageModal({ open: true, type: "success", title: "Success", message: msg });
    const showError = (msg) => setMessageModal({ open: true, type: "error", title: "Error", message: msg });

    return (
        <PageFade>
            <div className="bg-white shadow-lg rounded-2xl p-6 relative">
                {/* Header Section */}
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                    <h4 className="text-xl font-semibold text-blue">
                        {t('pageTitles.budgetHeadList') || "Add / Edit Budget Head"}
                    </h4>
                    <img src={closeButton} alt="Close" className="w-6 h-6 cursor-pointer hover:scale-110 transition-all" onClick={() => navigate(-1)} />
                </div>

                <div className="text-end mb-4">
                    <button className="btnAdd" onClick={() => setIsModalOpen(true)}>
                        {t('common.addNew')}
                    </button>
                </div>

                {/* Actions: Entries & Search */}
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-3">
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-600">{t('common.show')}:</label>
                        <select
                            value={size}
                            onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
                            className="border rounded px-2 py-1 text-sm outline-none focus:border-blue-400"
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <label className="text-sm font-medium text-gray-600">{t('common.entries')}</label>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            placeholder={t('common.search')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="border rounded px-3 py-1 text-sm outline-none focus:border-blue-400 w-full sm:w-64"
                        />
                    </div>
                </div>

                {/* Professional Table State Management */}
                <div className="overflow-x-auto border border-gray-100 shadow-sm">
                    <table className="w-full border text-sm ctable">
                        <thead className="bg-[#a0adf1] text-white">
                            <tr>
                                <th className="p-3 border-b border-black text-center w-16">{t('tables.srNo')}</th>
                                <th className="p-3 border-b border-black text-left">{t('accounts.payment.budgetHead') || "Budget Name"}</th>
                                <th className="p-3 border-b border-black text-center w-40">{t('common.date')}</th>
                                <th className="p-3 border-b border-black text-right w-56">{t('accounts.budgetHead.sanctionedAmount') || "Sanction Budget Amount"}</th>
                                <th className="p-3 border-b border-black text-right w-56">{t('accounts.payment.balanceAmount') || "Balance Amount"}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="p-10 text-center">
                                        <div className="flex justify-center items-center gap-3">
                                            <span className="text-gray-500 font-medium">{t('common.loading')}...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="text-center  bg-gray-50">

                                        <p className="mt-2 text-gray-500">{t('common.noRecords')}</p>

                                    </td>
                                </tr>
                            ) : (
                                data.map((item, idx) => (
                                    <tr key={item.id} className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-blue-50 transition-colors border-b`}>
                                        <td className="p-3 text-center border-r border-gray-200">{page * size + idx + 1}</td>
                                        <td className="p-3 border-r border-gray-200 font-semibold">{item.budgetName}</td>
                                        <td className="p-3 text-center border-r border-gray-200">{item.budgetDate}</td>
                                        <td className="p-3 text-right border-r border-gray-200 font-bold">
                                            {item.budgetAmount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className={`p-3 text-right ${item.balanceAmount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                            {item.balanceAmount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer Info + Pagination */}
                <div className="flex flex-col sm:flex-row justify-between items-center mt-6 text-sm">
                    <p className="text-gray-600">
                        {t('common.showing')} {data.length > 0 ? page * size + 1 : 0} {t('common.to')} {Math.min((page + 1) * size, totalElements)} {t('common.of')} {totalElements} {t('common.entries')}
                    </p>
                    <div className="flex gap-1 mt-2 sm:mt-0">
                        <button
                            className="px-3 py-1 border rounded disabled:opacity-50 font-bold hover:bg-gray-50"
                            onClick={() => setPage((p) => Math.max(p - 1, 0))}
                            disabled={page === 0}
                        >
                            {t('common.previous')}
                        </button>

                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setPage(i)}
                                className={`px-3 py-1 border rounded font-bold transition-all ${page === i
                                    ? "bg-gradient-to-b from-white to-[#dcdcdc] border border-[#979797] text-black shadow-sm"
                                    : "bg-white text-gray-600 hover:bg-gray-100"
                                    }`}
                            >
                                {i + 1}
                            </button>
                        )).slice(Math.max(0, page - 2), Math.min(totalPages, page + 3))}

                        <button
                            className="px-3 py-1 border rounded disabled:opacity-50 font-bold hover:bg-gray-50"
                            onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
                            disabled={page === totalPages - 1}
                        >
                            {t('common.next')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <BudgetHeadModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleFormSubmit}
            />

            <ConfirmActionModal
                open={confirmModal.open}
                title="Confirm Addition"
                message="Are you sure you want to add this new budget head record?"
                confirmText="SAVE"
                onConfirm={handleConfirmSave}
                onClose={() => setConfirmModal({ open: false, formData: null })}
            />

            <MessageModal
                open={messageModal.open}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
                {...messageModal}
            />
        </PageFade>
    );
};

export default BudgetHeadPage;